import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { observer } from 'mobx-react-lite'
import {
  Avatar, Box, Button, Chip, CircularProgress,
  IconButton, Paper, Switch, Tooltip, Typography,
  FormControl, Select, MenuItem, InputLabel,
} from '@mui/material'
import {
  Hub as ChannelIcon,
  Add as AddIcon,
  Refresh as SyncIcon,
  OpenInNew as ViewIcon,
  ErrorOutline as ErrorIcon,
  CheckCircleOutline as ActiveIcon,
  LinkOff as InactiveIcon,
} from '@mui/icons-material'
import { toast } from 'react-toastify'
import { DataTable, type Column } from '../components/ui/DataTable'
import { PageHeader } from '../components/shared/PageHeader'
import { ListToolbar } from '../components/shared/ListToolbar'
import { DeleteDialog } from '../components/shared/DeleteDialog'
import { PageActionButtons } from '../components/shared/PageActionButtons'
import { EditActionIcon, DeleteActionIcon } from '../components/ui/ActionIcons'
import {
  channelsApi,
  CHANNEL_TYPES, CHANNEL_TYPE_LABELS, CHANNEL_TYPE_COLORS,
  type Channel, type ChannelFilters, type ChannelType,
} from '../lib/api/channelsApi'
import { type ChannelFormData } from '../lib/schemas/channelSchema'
import { ChannelDrawer } from '../components/drawers/ChannelDrawer'
import { themeColors } from '../app/layout/theme'
import { authStore } from '../lib/stores/authStore'
import { EMPTY_UNIFIED_FILTERS, FilterDef, UnifiedFilters, countActiveFilters } from '../components/shared/UnifiedFilterDialog'
import { usePermission } from '../lib/hooks/usePermission'

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatRelative(dateStr: string | null): string {
  if (!dateStr) return '—'
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60_000)
  const hours = Math.floor(diff / 3_600_000)
  const days = Math.floor(diff / 86_400_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days < 7) return `${days}d ago`
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

function ChannelTypeBadge({ type }: { type: ChannelType }) {
  const color = CHANNEL_TYPE_COLORS[type]
  return (
    <Chip
      label={CHANNEL_TYPE_LABELS[type]}
      size="small"
      sx={{
        height: 20, fontSize: '10.5px', fontWeight: 600,
        bgcolor: `${color}18`, color,
        border: `1px solid ${color}40`,
        '& .MuiChip-label': { px: '6px' },
      }}
    />
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// Main Page
// ══════════════════════════════════════════════════════════════════════════════

const ChannelsPage = observer(function ChannelsPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { hasAction, isSuperAdmin } = usePermission()
  const canCreate = hasAction('/channels', 'create')
  const canEdit   = hasAction('/channels', 'update')
  const canDelete = hasAction('/channels', 'delete')

  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<UnifiedFilters>(EMPTY_UNIFIED_FILTERS)
  const [typeFilter, setTypeFilter] = useState<ChannelType | ''>('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem('crm_channels_page_size')
    return saved ? Number(saved) : 10
  })

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingChannel, setEditingChannel] = useState<Channel | null>(null)
  const [deletingChannel, setDeletingChannel] = useState<Channel | null>(null)

  const apiFilters: ChannelFilters = {
    search: search || undefined,
    types:  typeFilter ? [typeFilter] : undefined,
    page,
    pageSize,
  }

  const { data, isLoading } = useQuery({
    queryKey: ['channels', apiFilters],
    queryFn: () => channelsApi.getAll(apiFilters),
    placeholderData: prev => prev,
    staleTime: 0,
  })

  const channels = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = data?.totalPages ?? 1

  const invalidate = () => qc.invalidateQueries({ queryKey: ['channels'] })

  // ── Mutations ────────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (d: ChannelFormData) => channelsApi.create({
      name:                 d.name,
      type:                 d.type,
      accessToken:          d.accessToken || undefined,
      refreshToken:         d.refreshToken || undefined,
      webhookSecret:        d.webhookSecret || undefined,
      apiBaseUrl:           d.apiBaseUrl   || undefined,
      syncFrequencyMinutes: d.syncFrequencyMinutes,
      fieldMappings:        d.fieldMappings,
      orgId:                d.orgId || undefined,
      lobId:                d.lobId || undefined,
    }),
    onSuccess: (ch) => {
      invalidate()
      setDrawerOpen(false)
      toast.success('Channel created')
      navigate(`/channels/${ch.id}`)
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to create channel'),
  })

  const updateMutation = useMutation({
    mutationFn: (d: ChannelFormData) => channelsApi.update(editingChannel!.id, {
      name:                 d.name,
      type:                 d.type,
      accessToken:          d.accessToken  || null,
      refreshToken:         d.refreshToken || null,
      webhookSecret:        d.webhookSecret || null,
      apiBaseUrl:           d.apiBaseUrl   || null,
      syncFrequencyMinutes: d.syncFrequencyMinutes,
      fieldMappings:        d.fieldMappings,
    }),
    onSuccess: () => {
      invalidate()
      setEditingChannel(null)
      toast.success('Channel updated')
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to update channel'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => channelsApi.delete(id),
    onSuccess: () => { invalidate(); setDeletingChannel(null); toast.success('Channel deleted') },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to delete channel'),
  })

  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, current, fieldMappings }: { id: string; current: string; fieldMappings?: any[] }) => {
      const nextStatus = current === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
      if (nextStatus === 'ACTIVE' && (!fieldMappings || fieldMappings.length === 0)) {
        toast.error('Field mappings must be configured before activating this channel.')
        return Promise.reject(new Error('Field mappings not configured'))
      }
      return channelsApi.toggleStatus(id, nextStatus)
    },
    onSuccess: (ch) => {
      invalidate()
      toast.success(`Channel ${ch.status === 'ACTIVE' ? 'activated' : 'deactivated'}`)
    },
    onError: (e: any) => {
      if (e.message !== 'Field mappings not configured') {
        toast.error(e?.response?.data?.message ?? 'Failed to toggle status')
      }
    },
  })

  const syncMutation = useMutation({
    mutationFn: (id: string) => channelsApi.sync(id),
    onSuccess: () => {
      invalidate()
      toast.info('Sync started — open the channel to watch live progress')
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Sync failed'),
  })

  // ── Summary stats ────────────────────────────────────────────────────────────

  const activeCount   = channels.filter(c => c.status === 'ACTIVE').length
  const inactiveCount = channels.filter(c => c.status === 'INACTIVE').length
  const errorCount    = channels.reduce((acc, c) => acc + (c.errorCount ?? 0), 0)

  // ── Columns ──────────────────────────────────────────────────────────────────

  const columns: Column[] = [
    {
      id: 'name', label: 'Channel Name', sortable: true, minWidth: 200,
      render: (_v, ch) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
          <Box sx={{
            width: 34, height: 34, borderRadius: '8px', flexShrink: 0,
            bgcolor: `${CHANNEL_TYPE_COLORS[ch.type as ChannelType]}18`,
            border: `1.5px solid ${CHANNEL_TYPE_COLORS[ch.type as ChannelType]}40`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: CHANNEL_TYPE_COLORS[ch.type as ChannelType] }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '13px', color: themeColors.primary, lineHeight: 1.3 }}>
              {ch.name}
            </Typography>
            {ch.organizationName && (
              <Typography sx={{ fontSize: '11px', color: themeColors.text.muted }}>
                {ch.organizationName}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      id: 'type', label: 'Type', width: 140,
      render: (_v, ch) => <ChannelTypeBadge type={ch.type as ChannelType} />,
    },
    {
      id: 'status', label: 'Status', width: 110,
      render: (_v, ch) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
          onClick={e => e.stopPropagation()}>
          <Switch
            size="small"
            checked={ch.status === 'ACTIVE'}
            disabled={!canEdit || toggleStatusMutation.isPending}
            onChange={() => toggleStatusMutation.mutate({ id: ch.id, current: ch.status, fieldMappings: ch.fieldMappings })}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: '#10b981' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#10b981' },
            }}
          />
          <Typography sx={{ fontSize: '12px', fontWeight: 600,
            color: ch.status === 'ACTIVE' ? '#10b981' : themeColors.text.muted }}>
            {ch.status === 'ACTIVE' ? 'Active' : 'Inactive'}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'leadSources', label: 'Lead Sources', width: 120,
      render: (_v, ch) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: themeColors.text.primary }}>
            {ch.leadSources?.length ?? 0}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: themeColors.text.muted }}>sources</Typography>
        </Box>
      ),
    },
    {
      id: 'errorCount', label: 'Errors', width: 90,
      render: (_v, ch) => (
        ch.errorCount > 0 ? (
          <Chip
            icon={<ErrorIcon sx={{ fontSize: 12 }} />}
            label={ch.errorCount}
            size="small"
            sx={{
              height: 20, fontSize: '10.5px', fontWeight: 700,
              bgcolor: '#fee2e2', color: '#dc2626',
              border: '1px solid #fca5a5',
              '& .MuiChip-label': { px: '4px' },
              '& .MuiChip-icon': { ml: '4px', color: '#dc2626' },
            }}
          />
        ) : (
          <Typography sx={{ fontSize: '12px', color: '#10b981' }}>—</Typography>
        )
      ),
    },
    {
      id: 'lastSyncAt', label: 'Last Sync', minWidth: 120,
      render: (_v, ch) => (
        <Typography sx={{ fontSize: '12.5px', color: themeColors.text.secondary }}>
          {formatRelative(ch.lastSyncAt)}
        </Typography>
      ),
    },
    {
      id: 'lastSuccessfulSyncAt', label: 'Last Success', minWidth: 120,
      render: (_v, ch) => (
        <Typography sx={{ fontSize: '12.5px', color: ch.lastSuccessfulSyncAt ? '#10b981' : themeColors.text.muted }}>
          {ch.lastSuccessfulSyncAt ? formatRelative(ch.lastSuccessfulSyncAt) : '—'}
        </Typography>
      ),
    },
    {
      id: 'lastSyncStatus', label: 'Sync Status', width: 110,
      render: (_v, ch) => {
        if (!ch.lastSyncStatus) return <Typography sx={{ fontSize: '12px', color: themeColors.text.muted }}>—</Typography>
        const cfg: Record<string, { bg: string; color: string }> = {
          SUCCESS:    { bg: '#f0fdf4', color: '#15803d' },
          FAILED:     { bg: '#fef2f2', color: '#dc2626' },
          PROCESSING: { bg: '#eff6ff', color: '#2563eb' },
        }
        const c = cfg[ch.lastSyncStatus] ?? { bg: '#f5f7fa', color: themeColors.text.muted }
        return (
          <Chip
            label={ch.lastSyncStatus}
            size="small"
            sx={{
              height: 20, fontSize: '10px', fontWeight: 700,
              bgcolor: c.bg, color: c.color,
              border: `1px solid ${c.color}40`,
              '& .MuiChip-label': { px: '6px' },
            }}
          />
        )
      },
    },
    {
      id: 'createdBy', label: 'Created By', minWidth: 130,
      render: (_v, ch) => (
        ch.createdBy ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Avatar sx={{ width: 22, height: 22, fontSize: '9px', fontWeight: 700, bgcolor: themeColors.primary }}>
              {ch.createdBy.name.charAt(0).toUpperCase()}
            </Avatar>
            <Typography sx={{ fontSize: '12.5px', color: themeColors.text.secondary }} noWrap>
              {ch.createdBy.name}
            </Typography>
          </Box>
        ) : <Typography sx={{ fontSize: '12.5px', color: themeColors.text.muted }}>—</Typography>
      ),
    },
    {
      id: 'actions', label: 'Actions', align: 'right' as const, width: 130,
      render: (_v, ch) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }}
          onClick={e => e.stopPropagation()}>

          {/* Sync */}
          <Tooltip title={(ch.fieldMappings?.length ?? 0) === 0 ? "Configure Field Mapping before syncing this channel" : "Sync now"}>
            <span>
              <IconButton size="small"
                disabled={ch.status !== 'ACTIVE' || syncMutation.isPending || (ch.fieldMappings?.length ?? 0) === 0}
                onClick={() => syncMutation.mutate(ch.id)}
                sx={{ color: '#7A7580', '&:hover': { color: '#0070d2', bgcolor: '#e0f0ff' } }}>
                {syncMutation.isPending
                  ? <CircularProgress size={13} />
                  : <SyncIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </span>
          </Tooltip>

          {/* View */}
          <Tooltip title="View details">
            <IconButton size="small"
              onClick={() => navigate(`/channels/${ch.id}`)}
              sx={{ color: '#7A7580', '&:hover': { color: themeColors.primary, bgcolor: themeColors.primaryLight + '15' } }}>
              <ViewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>

          {/* Edit */}
          {canEdit && (
            <Tooltip title="Edit channel">
              <IconButton size="small"
                onClick={() => setEditingChannel(ch)}
                sx={{ color: '#7A7580', '&:hover': { color: '#2F1E57', bgcolor: '#F2EEF9' } }}>
                <EditActionIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* Delete */}
          {canDelete && (
            <Tooltip title="Delete channel">
              <IconButton size="small"
                onClick={() => setDeletingChannel(ch)}
                sx={{ color: '#D92D20', '&:hover': { bgcolor: '#FFE5E5' } }}>
                <DeleteActionIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        title="Channel Integrations"
        stats={[
          { label: 'Total',    value: total,        color: themeColors.primary },
          { label: 'Active',   value: activeCount,   color: '#10b981' },
          { label: 'Inactive', value: inactiveCount, color: themeColors.text.muted },
          { label: 'Errors',   value: errorCount,    color: '#dc2626' },
        ]}
        actions={canCreate ? (
          <PageActionButtons
            onNew={() => setDrawerOpen(true)}
            newLabel="New Channel"
          />
        ) : undefined}
      />

      {/* ── Pinned toolbar (search + type filter) ── */}
      <Box sx={{ px: 3, pt: 3, pb: 0, flexShrink: 0 }}>
        {/* Toolbar + Type Filter */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <ListToolbar
              search={search}
              onSearch={v => { setSearch(v); setPage(1) }}
              searchPlaceholder="Search channels..."
              filterDefs={[]}
              filters={filters}
              onFilterChange={setFilters}
            />
          </Box>
          {/* Type quick-filter */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel sx={{ fontSize: '13px' }}>All Types</InputLabel>
            <Select
              label="All Types"
              value={typeFilter}
              onChange={e => { setTypeFilter(e.target.value as any); setPage(1) }}
              sx={{
                borderRadius: '8px', fontSize: '13px',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.border },
              }}
            >
              <MenuItem value="" sx={{ fontSize: '13px' }}>All Types</MenuItem>
              {CHANNEL_TYPES.map(t => (
                <MenuItem key={t} value={t} sx={{ fontSize: '13px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: CHANNEL_TYPE_COLORS[t] }} />
                    {CHANNEL_TYPE_LABELS[t]}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ flex: 1, px: 3, pb: 3, pt: 2, display: 'flex', flexDirection: 'column', minHeight: 0 }}>

        {/* Channel type overview cards */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
          {CHANNEL_TYPES.map(t => {
            const count = channels.filter(c => c.type === t).length
            const color = CHANNEL_TYPE_COLORS[t]
            const isActive = typeFilter === t
            return (
              <Box
                key={t}
                onClick={() => setTypeFilter(isActive ? '' : t)}
                sx={{
                  px: 1.75, py: 1, borderRadius: '8px', cursor: 'pointer',
                  bgcolor: isActive ? `${color}12` : '#fff',
                  border: `1.5px solid ${isActive ? color : themeColors.border}`,
                  display: 'flex', alignItems: 'center', gap: 1,
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: color, bgcolor: `${color}08` },
                }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: isActive ? color : themeColors.text.secondary }}>
                  {CHANNEL_TYPE_LABELS[t]}
                </Typography>
                <Box sx={{
                  px: '7px', py: '1px', borderRadius: '20px',
                  bgcolor: isActive ? `${color}20` : '#f5f7fa',
                }}>
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: isActive ? color : themeColors.text.secondary }}>
                    {count}
                  </Typography>
                </Box>
              </Box>
            )
          })}
        </Box>

        {/* Data table */}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <DataTable
            columns={columns}
            rows={channels}
            isLoading={isLoading}
            onRowClick={ch => navigate(`/channels/${ch.id}`)}
            emptyMessage={search || typeFilter ? 'No channels match your filters' : 'No channels yet'}
            emptyHint={search || typeFilter ? 'Try adjusting your filters' : 'Connect your first lead source to get started'}
            emptyIcon={<ChannelIcon />}
            pagination={{
              page,
              pageSize,
              total,
              totalPages,
              itemLabel: 'channels',
              onPageChange: p => setPage(p),
              onPageSizeChange: s => { setPageSize(s); localStorage.setItem('crm_channels_page_size', String(s)); setPage(1) },
              pageSizeOptions: [10, 20, 50],
            }}
          />
        </Box>
      </Box>

      {/* ── Create / Edit Drawer ── */}
      <ChannelDrawer
        open={drawerOpen || !!editingChannel}
        mode={editingChannel ? 'edit' : 'create'}
        onClose={() => { setDrawerOpen(false); setEditingChannel(null) }}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
        onSubmit={async (data) => {
          if (editingChannel) await updateMutation.mutateAsync(data)
          else await createMutation.mutateAsync(data)
        }}
        initialData={editingChannel ? {
          name:                 editingChannel.name,
          type:                 editingChannel.type as any,
          accessToken:          editingChannel.accessToken ?? '',
          refreshToken:         editingChannel.refreshToken ?? '',
          webhookSecret:        editingChannel.webhookSecret ?? '',
          apiBaseUrl:           editingChannel.apiBaseUrl ?? '',
          syncFrequencyMinutes: editingChannel.syncFrequencyMinutes ?? 5,
          fieldMappings:        editingChannel.fieldMappings ?? [],
          orgId:                editingChannel.organizationId ?? '',
          lobId:                editingChannel.lobId ?? '',
        } : undefined}
      />

      {/* ── Delete confirmation ── */}
      <DeleteDialog
        open={!!deletingChannel}
        title="Delete Channel"
        name={deletingChannel?.name}
        description={
          deletingChannel
            ? `Deleting "${deletingChannel.name}" will remove all associated lead sources and mappings. This cannot be undone.`
            : undefined
        }
        onClose={() => setDeletingChannel(null)}
        onConfirm={() => deletingChannel && deleteMutation.mutate(deletingChannel.id)}
        isDeleting={deleteMutation.isPending}
      />
    </Box>
  )
})

export default ChannelsPage
